/**
 * PNG export functionality for the musician graph.
 * Handles converting the SVG to a high-resolution PNG with watermark.
 */
import * as d3 from 'd3';

/**
 * Export the graph SVG as a high-resolution PNG.
 *
 * @param {SVGElement} svgElement - The SVG DOM element
 * @param {boolean} withOverlay - Whether to show cluster overlays (true) or musician names (false)
 * @param {boolean} returnCanvas - If true, returns the canvas element instead of triggering download
 * @param {boolean} mobile - Whether the device is mobile
 * @returns {Promise<HTMLCanvasElement|null>}
 */
export const exportGraphAsPNG = (svgElement, withOverlay = true, returnCanvas = false, mobile = false) => {
    const RESOLUTION_SCALE = 4;

    const nodes = d3.select(svgElement).selectAll('.nodes circle').nodes();
    if (!nodes.length) return Promise.resolve(null);

    const currentOverlayOpacity = d3.select(svgElement)
        .select('.cluster-overlays')
        .style('opacity');
    const currentLabelOpacity = d3.select(svgElement)
        .select('.cluster-labels')
        .style('opacity');

    const musicianLabelGroups = d3.select(svgElement)
        .selectAll('.label-backgrounds, .label-texts');

    const currentMusicianLabelsOpacity = [];
    musicianLabelGroups.each(function () {
        currentMusicianLabelsOpacity.push(d3.select(this).style('opacity'));
    });

    if (withOverlay) {
        d3.select(svgElement)
            .select('.cluster-overlays')
            .style('opacity', 1);
        d3.select(svgElement)
            .select('.cluster-labels')
            .style('opacity', 1);
        musicianLabelGroups.style('opacity', 0);
    } else {
        d3.select(svgElement)
            .select('.cluster-overlays')
            .style('opacity', 0);
        d3.select(svgElement)
            .select('.cluster-labels')
            .style('opacity', 0);
        musicianLabelGroups.style('opacity', 1);
    }

    const nodeBounds = nodes.reduce((bounds, node) => {
        const x = parseFloat(node.getAttribute('cx'));
        const y = parseFloat(node.getAttribute('cy'));
        const r = parseFloat(node.getAttribute('r'));

        return {
            minX: Math.min(bounds.minX, x - r),
            minY: Math.min(bounds.minY, y - r),
            maxX: Math.max(bounds.maxX, x + r),
            maxY: Math.max(bounds.maxY, y + r)
        };
    }, {
        minX: Infinity,
        minY: Infinity,
        maxX: -Infinity,
        maxY: -Infinity
    });

    const padding = 1 * RESOLUTION_SCALE;
    const width = nodeBounds.maxX - nodeBounds.minX + (padding * 2);
    const height = nodeBounds.maxY - nodeBounds.minY + (padding * 2);

    const scale = Math.min(
        window.innerWidth / width,
        window.innerHeight / height
    ) * RESOLUTION_SCALE * 0.85;

    const tempSvg = svgElement.cloneNode(true);
    const container = tempSvg.querySelector('g');
    const transform = d3.zoomIdentity
        .translate(
            (window.innerWidth * RESOLUTION_SCALE / 2) - ((nodeBounds.minX + nodeBounds.maxX) / 2 * scale),
            (window.innerHeight * RESOLUTION_SCALE / 2) - ((nodeBounds.minY + nodeBounds.maxY) / 2 * scale)
        )
        .scale(scale);

    container.setAttribute('transform', transform.toString());

    tempSvg.setAttribute('width', window.innerWidth * RESOLUTION_SCALE);
    tempSvg.setAttribute('height', window.innerHeight * RESOLUTION_SCALE);
    tempSvg.setAttribute('viewBox', `0 0 ${window.innerWidth * RESOLUTION_SCALE} ${window.innerHeight * RESOLUTION_SCALE}`);

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = window.innerWidth * RESOLUTION_SCALE;
    canvas.height = window.innerHeight * RESOLUTION_SCALE;

    context.scale(RESOLUTION_SCALE, RESOLUTION_SCALE);

    const gradient = context.createLinearGradient(0, 0, canvas.width / RESOLUTION_SCALE, canvas.height / RESOLUTION_SCALE);
    gradient.addColorStop(0, '#1c0c24');
    gradient.addColorStop(1, '#100e21');

    context.save();
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width / RESOLUTION_SCALE, canvas.height / RESOLUTION_SCALE);
    context.restore();

    const svgData = new XMLSerializer().serializeToString(tempSvg);
    const img = new Image();
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const restoreStates = () => {
        d3.select(svgElement)
            .select('.cluster-overlays')
            .style('opacity', currentOverlayOpacity);
        d3.select(svgElement)
            .select('.cluster-labels')
            .style('opacity', currentLabelOpacity);

        let index = 0;
        musicianLabelGroups.style('opacity', function () {
            return currentMusicianLabelsOpacity[index++] || null;
        });
    };

    return new Promise((resolve) => {
        img.onload = () => {
            context.drawImage(img, 0, 0, canvas.width / RESOLUTION_SCALE, canvas.height / RESOLUTION_SCALE);

            // Add "musicgrapher.com" watermark
            context.save();
            context.fillStyle = 'white';
            context.font = `${28}px Arial`;
            context.textAlign = 'right';
            context.textBaseline = 'bottom';

            const textPadding = 20;
            const text = 'musicgrapher.com';
            const x = (canvas.width / RESOLUTION_SCALE) - textPadding;
            const y = (canvas.height / RESOLUTION_SCALE) - textPadding - (mobile ? 10 : 0);

            context.fillText(text, x, y);
            context.restore();

            if (returnCanvas) {
                URL.revokeObjectURL(url);
                restoreStates();
                resolve(canvas);
                return;
            }

            const pngUrl = canvas.toDataURL('image/png');
            const downloadLink = document.createElement('a');
            downloadLink.download = withOverlay ? 'musician-graph-overview.png' : 'musician-graph-detailed.png';
            downloadLink.href = pngUrl;
            downloadLink.click();

            URL.revokeObjectURL(url);
            restoreStates();
            resolve(null);
        };

        img.onerror = () => {
            console.error('Error loading image for download');
            URL.revokeObjectURL(url);
            restoreStates();
            resolve(null);
        };

        img.src = url;
    });
};
